import { ITransformerSerialType, TransformerDeviceType } from '@/models/balance'
import { PaginationDTO } from '@/models/table'
import { useLanguageStore } from '@/stores/LanguageStore'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { isValidDate } from './moment'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

export const formatDateAndHour = (date: string | undefined) => {
  if (!date || date === undefined || !isValidDate(date)) {
    return useLanguageStore().t('general.noInformation')
  }
  return dayjs(date).format('DD/MM/YYYY - HH:mm:ss')
}

export const formatDateAndHourInTimeZone = (date: string | undefined, timezone: string) => {
  if (!date || date === undefined || !isValidDate(date)) {
    return useLanguageStore().t('general.noInformation')
  }
  return dayjs.utc(date).tz(timezone).format('DD/MM/YYYY - HH:mm:ss')
}

export const isDate = (key: any) => {
  return key === 'updatedAt' || key === 'createdAt'
}

export function formatIP(str: any) {
  str = str.toString()
  const segments = str.match(/.{1,4}/g)
  return segments.join(':')
}

export function formatMac(str: any) {
  str = str.toString()
  const segments = str.match(/.{1,2}/g)
  return segments.join(':')
}

export const plainText = (value: string) => {
  return value.replaceAll(':', '').toUpperCase()
}

export const formatDate = (date: string) => {
  return dayjs(date).tz('America/Sao_Paulo').format('DD/MM/YYYY')
}

export const formatDateRaw = (date: string) => {
  return dayjs.utc(date).format('DD/MM/YYYY')
}
export const formatDateDisplay = (date: string | undefined) => {
  if (!date) {
    return '--'
  }
  return dayjs(date).format('DD MM YYYY')
}
export const formatHourDisplay = (date: string | undefined) => {
  if (!date) {
    return '--'
  }
  return dayjs(date).format('HH mm ss')
}
export const pipeDateTime = (dateTime: Date, utc = false): string => {
  const { lang } = useLanguageStore()
  const DEFAULT_DATE = '0001-01-01T00:00:00.000Z'

  if (dateTime && dateTime.toString() === DEFAULT_DATE) return 'Nunca Comunicou ou Data Invalida'
  return dayjs(dateTime).utc(utc).locale(lang).format('l LTS')
}

export const formatMonth = (date: string | Date) => {
  return dayjs(date).format('YYYY-MM')
}

const ONEHOURINMINUTES = 60
export const parserUplinkPeriod = (timeInMinutes: number): string => {
  if (timeInMinutes == null) return ''

  const hour = Math.floor(timeInMinutes / ONEHOURINMINUTES)
  const minutes = timeInMinutes % ONEHOURINMINUTES

  if (hour > 0) {
    return `${hour}H:${minutes}M`
  } else {
    return `${minutes}M`
  }
}

const KEYREGEXREPLACE = /(?<=[A-Za-z0-9]{3})[A-Za-z0-9](?=[A-Za-z0-9]{3})/gm
export const encryptKeys = (key: string): string => {
  return key.replace(KEYREGEXREPLACE, '*')
}

export const axiosParamsSerializer = (params: Record<string, any>) => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => {
        searchParams.append(key, v)
      })
    } else {
      searchParams.append(key, value)
    }
  })
  return searchParams.toString()
}

export const decimalToHexString = (intNumber: number): string => {
  return '0x' + intNumber.toString(16).toUpperCase()
}

export const formatTemplateResponse = (name: string, value: number): string => {
  return `${name} (${decimalToHexString(value)})`
}

export const setPages = (pagination: PaginationDTO<any>, data: any): PaginationDTO<any> => {
  pagination.page = data?.page
  pagination.pageSize = data?.pageSize
  pagination.numberOfItems = data?.numberOfItems
  pagination.numberOfPages = data?.numberOfPages
  return pagination
}

export const ipRegex = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/

export const removeMask = (item?: string) => {
  if (item) {
    return item.replace(/[^0-9a-zA-Z.]/g, '')
  }
}

export const formatSerial = (value: any): string => {
  const str = typeof value === 'string' ? value : String(value)
  const cleaned = str.replace(/^0+/, '')
  return cleaned || '0'
}

export function validateKeys(key: string, rawValue: any): { value: string; error?: string } {
  const lengthNeeded: Record<string, number[]> = {
    ak: [16, 32],
    ek: [16, 32],
    mk: [16, 32],
    lls2: [16, 20],
  }

  const value = String(rawValue ?? '')
  const expectedLength = lengthNeeded[key]

  const error =
    expectedLength && !expectedLength.includes(value.length)
      ? `Tamanho inválido de "${key}": esperado ${expectedLength.join(',')} caracteres, recebido ${value.length}`
      : undefined

  return { value, error }
}

export const formatNumValues = (val: unknown): string => {
  const num = Number(val)
  return isNaN(num) ? '--' : (num / 1000).toFixed(2)
}

export const extractSerials = (listOfItems: any[]): string[] => {
  return listOfItems.map((item) => item.serial)
}

export const validSerial = (listOfItems: any[]): boolean => {
  return listOfItems.some((item) => typeof item.serial === 'string' && item.serial.trim() !== '')
}
export const formatNumToBalance = (val: unknown): string => {
  const num = Number(val)
  return isNaN(num) ? '--' : (num / 100).toFixed(2)
}

export const getCheckedMetersToTransformers = (meters: any): ITransformerSerialType[] => {
  return meters.map((meter: any) => {
    const rawType = (meter as any)?.type
    const parsedType = rawType !== undefined && rawType !== null ? Number(rawType) : undefined
    const typeValue = parsedType === 0 || parsedType === 1 ? parsedType : TransformerDeviceType.SMI

    return {
      serial: meter?.serial || '',
      type: typeValue as TransformerDeviceType,
      updatedAt: meter?.updatedAt || new Date(),
    }
  })
}

export const formatToSendFilter = (date: any) => {
  return dayjs(date).format('YYYY-MM-DDTHH:mm:ss')
}

export const formatSeconds = (totalSeconds: number) => {
  const secondsInMinute = 60
  const secondsInHour = 3600
  const secondsInDay = 86400
  const secondsInMonth = 2592000
  const secondsInYear = 31536000

  const years = Math.floor(totalSeconds / secondsInYear)
  totalSeconds %= secondsInYear

  const months = Math.floor(totalSeconds / secondsInMonth)
  totalSeconds %= secondsInMonth

  const days = Math.floor(totalSeconds / secondsInDay)
  totalSeconds %= secondsInDay

  const hours = Math.floor(totalSeconds / secondsInHour)
  totalSeconds %= secondsInHour

  const minutes = Math.floor(totalSeconds / secondsInMinute)
  const seconds = totalSeconds % secondsInMinute

  let result = ''
  if (years > 0) result += `${years} Anos  `
  if (months > 0) result += `${months} Mês(es) `
  if (days > 0) result += `${days} Dias `
  if (hours > 0) result += `${hours}h`
  if (minutes > 0) result += `${minutes}m`

  result += `${seconds}s`

  return result
}

export const lowercaseFirstLetter = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1)
}
export const transformKeysFirstLetterLower = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(transformKeysFirstLetterLower)
  } else if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      acc[lowercaseFirstLetter(key)] = transformKeysFirstLetterLower(value)
      return acc
    }, {} as any)
  }
  return obj
}

export const formatDateRange = (dates?: Array<Date | null | undefined>) => {
  if (!dates || dates.length === 0) return []

  const startDate =
    dates[0] instanceof Date && !Number.isNaN(dates[0].getTime())
      ? new Date(dates[0])
      : undefined

  const endDate =
    dates[1] instanceof Date && !Number.isNaN(dates[1].getTime())
      ? new Date(dates[1])
      : undefined

  if (!startDate) return []

  const startOfDay = new Date(startDate)
  startOfDay.setHours(0, 0, 0, 0) // primeira hora do dia

  if (!endDate) {
    const endOfSameDay = new Date(startOfDay)
    endOfSameDay.setHours(23, 59, 59, 999)
    return [startOfDay, endOfSameDay]
  }

  const endOfDay = new Date(endDate)
  endOfDay.setHours(23, 59, 59, 999)

  return [startOfDay, endOfDay]
}

export const formatDateRangeForPicker = (
  dates?: Array<Date | null | undefined> | null,
) => {
  if (!dates || dates.length === 0) return []

  return dates
    .filter((date): date is Date => date instanceof Date && !Number.isNaN(date.getTime()))
    .map((date) => {
      const parsedDate = date instanceof Date ? date : new Date(date)

      return new Date(
        parsedDate.getUTCFullYear(),
        parsedDate.getUTCMonth(),
        parsedDate.getUTCDate(),
        0,
        0,
        0,
        0,
      )
    })
}

export const isValidDateValue = (value: unknown): value is Date => {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

export const hasCompleteDateRange = (
  dates?: Array<Date | null | undefined> | null,
): dates is [Date, Date] => {
  return !!dates && isValidDateValue(dates[0]) && isValidDateValue(dates[1])
}

export const serializeDateRangeToIso = (
  dates?: Array<Date | null | undefined> | null,
) => {
  if (!dates || dates.length === 0) {
    return {
      startDate: undefined,
      endDate: undefined,
    }
  }

  const validDates = dates.filter(isValidDateValue)
  const normalizedRange = formatDateRange(validDates)

  return {
    startDate: isValidDateValue(dates[0]) && normalizedRange[0]
      ? normalizedRange[0].toISOString()
      : undefined,
    endDate: isValidDateValue(dates[1]) && normalizedRange[1]
      ? normalizedRange[1].toISOString()
      : undefined,
  }
}

export function toLocalDateWithoutTimezone(dateString: string) {
  const date = new Date(dateString)

  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )
}

export const getReportLocale = (): string => {
  const { lang } = useLanguageStore()
  switch (lang) {
    case 'en':
      return 'en-US'
    case 'es':
      return 'es-ES'
    default:
      return 'pt-BR'
  }
}

export const parseLocaleNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === '') return 0
  if (typeof value === 'number') return value
  const normalized = value.replace(/\./g, '').replace(',', '.')
  const parsed = Number(normalized)
  return Number.isNaN(parsed) ? 0 : parsed
}

export const formatLocaleNumber = (value: number): string => {
  return new Intl.NumberFormat(getReportLocale(), { maximumFractionDigits: 2 }).format(value)
}

export const formatLocalePercent = (value: number): string => {
  return new Intl.NumberFormat(getReportLocale(), { maximumFractionDigits: 1 }).format(value)
}

const toValidDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const formatLocaleDate = (value: string | Date | null | undefined): string => {
  const date = toValidDate(value)
  if (!date) return '-'
  return new Intl.DateTimeFormat(getReportLocale()).format(date)
}

export const formatLocaleMonthYear = (value: string | Date | null | undefined): string | null => {
  const date = toValidDate(value)
  if (!date) return null
  return new Intl.DateTimeFormat(getReportLocale(), {
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}

export const formatLocaleDayMonthYear = (
  value: string | Date | null | undefined,
): string | null => {
  const date = toValidDate(value)
  if (!date) return null
  return new Intl.DateTimeFormat(getReportLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date)
}
